import { MAG_LABELS } from '../lib/scoring'

/**
 * The crowd curve: where everyone guessed, by order of magnitude. The game's mascot.
 * Bins are 0..18 (log10). Marks the truth and (optionally) you.
 */
export function Curve({ bins, truthBin, youBin }: { bins: Record<string, number>; truthBin: number; youBin?: number; total?: number }) {
  const W = 440, H = 150, padB = 26, padT = 22
  const n = MAG_LABELS.length
  const counts = Array.from({ length: n }, (_, i) => bins[i] ?? 0)
  // light smoothing so it reads as a curve, not a comb
  const sm = counts.map((_, i) => (counts[i - 1] ?? 0) * 0.25 + counts[i] + (counts[i + 1] ?? 0) * 0.25)
  const max = Math.max(1, ...sm)
  const x = (i: number) => (i / (n - 1)) * (W - 20) + 10
  const y = (v: number) => H - padB - (v / max) * (H - padB - padT)
  const pts = sm.map((v, i) => [x(i), y(v)] as const)
  let d = `M ${pts[0][0]} ${pts[0][1]}`
  for (let i = 1; i < pts.length; i++) {
    const [x0, y0] = pts[i - 1], [x1, y1] = pts[i]; const cx = (x0 + x1) / 2
    d += ` C ${cx} ${y0}, ${cx} ${y1}, ${x1} ${y1}`
  }
  const area = `${d} L ${x(n - 1)} ${H - padB} L ${x(0)} ${H - padB} Z`
  const labelEvery = [0, 3, 6, 9, 12, 15, 18]
  return (
    <div>
      <svg className="curve" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Where everyone guessed">
        <defs>
          <linearGradient id="cg" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#F2F4FF" stopOpacity="0.35" /><stop offset="1" stopColor="#F2F4FF" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#cg)" />
        <path d={d} fill="none" stroke="#F2F4FF" strokeWidth="2" strokeLinejoin="round" />
        <line x1={x(truthBin)} x2={x(truthBin)} y1={padT - 4} y2={H - padB} stroke="#B4FF39" strokeWidth="2.5" />
        <text x={x(truthBin)} y={padT - 9} fontSize="12" fill="#B4FF39" textAnchor="middle" fontWeight="700">truth</text>
        {youBin != null && (() => {
          const close = Math.abs(x(youBin) - x(truthBin)) < 46
          // when the two markers crowd each other, "you" gets its own line below the curve
          const ly = close ? H - padB + 1 : padT - 9
          const anchor = youBin > 15 ? 'end' : youBin < 3 ? 'start' : 'middle'
          return (
            <g>
              <line x1={x(youBin)} x2={x(youBin)} y1={padT - 4} y2={H - padB} stroke="#F2F4FF" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.7" />
              <circle cx={x(youBin)} cy={y(sm[youBin])} r="6.5" fill="#070A14" stroke="#F2F4FF" strokeWidth="2.5" />
              <text x={x(youBin)} y={ly} fontSize="12" fill="#F2F4FF" textAnchor={anchor} fontWeight="700"
                stroke="#070A14" strokeWidth="4" paintOrder="stroke">you</text>
            </g>
          )
        })()}
        {labelEvery.map((i) => <text key={i} x={x(i)} y={H - 8} fontSize="11" fill="rgba(242,244,255,0.45)" textAnchor="middle">{MAG_LABELS[i]}</text>)}
      </svg>
    </div>
  )
}
